// src/auth/useSetPasswordConsents.js — what /set-password asks besides the password (v221).
//
// ⭐ Two PUBLIC reads when the page opens with a usable token: GET /legal/current (useCurrentTerms — the REQUIRED
//    terms box) and POST /auth/set-password/info (whether to show the OPTIONAL marketing box).
//    Contract: <BACKEND_FOLDER>/docs/frontend/set-password-flow.md.
// ⛔ /info never spends the link. Its 400 SET_PASSWORD_TOKEN_INVALID means the link is dead → onDeadLink(reason)
//    (the caller's invalid panel, before anyone types). Any other /info failure only hides the marketing box.
// ⛔ marketing_opt_in is sent ONLY when the box was shown (false when left unticked); the BE keeps the first answer.
import { useEffect, useRef, useState } from 'react';
import { fetchSetPasswordInfo } from './passwordResetApi.js';
import useCurrentTerms from '../legal/useCurrentTerms.js';

const INVALID_CODE = 'SET_PASSWORD_TOKEN_INVALID';

export default function useSetPasswordConsents({ token, enabled, onDeadLink }) {
  const terms = useCurrentTerms(enabled);
  const [askMarketing, setAskMarketing] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const deadRef = useRef(onDeadLink);
  deadRef.current = onDeadLink;

  useEffect(() => {
    if (!enabled) return undefined;
    let current = true;   // a stale answer never lands
    setAskMarketing(false);
    setMarketing(false);
    Promise.resolve(fetchSetPasswordInfo(token))
      .then((info) => { if (current) setAskMarketing(info?.askMarketingOptIn === true); })
      .catch((err) => {
        if (current && err?.status === 400 && err?.code === INVALID_CODE) deadRef.current?.(err.reason);
      });
    return () => { current = false; };
  }, [enabled, token]);

  return {
    terms,
    marketing: { shown: askMarketing, value: marketing, onChange: setMarketing },
    ready: terms.canProceed,
    submitFields: () => ({ terms: terms.terms, ...(askMarketing ? { marketingOptIn: marketing } : {}) }),
  };
}
